/**
 * Current-session state.
 *
 * Server state, so TanStack Query owns it rather than a global store — the source of truth is the
 * session cookie and the server's answer about it, and duplicating that into client state is how
 * "signed in" and "actually signed in" drift apart (docs/engineering/09-frontend.md).
 *
 * `account === null` means anonymous, which is a normal state in this product and never an error —
 * and it does **not** mean unauthorized: a Participant holding a Capability Link has real authority
 * with no Account behind it, which is why `capability` is reported alongside rather than folded in.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchMe, signOut, type CurrentAccount, type CurrentCapability } from './api';

export const ME_QUERY_KEY = ['auth', 'me'] as const;

export interface AuthState {
  account: CurrentAccount | null;
  /** Held instead of, or as well as, an Account. Neither masks the other. */
  capability: CurrentCapability | null;
  isLoading: boolean;
  isSignedIn: boolean;
}

export function useAuth(): AuthState {
  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    // The session can be revoked server-side at any moment — by a password reset, by "sign out
    // everywhere", by an administrator disabling the account. Refetching on focus keeps a tab that
    // has been sitting open from acting on stale authority.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  });

  return {
    account: query.data?.account ?? null,
    capability: query.data?.capability ?? null,
    isLoading: query.isPending,
    isSignedIn: query.data?.account != null,
  };
}

export function useSignOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signOut,
    onSettled: async () => {
      // Cleared on settle, not just on success: if sign-out failed because the session was already
      // gone, the cached identity is stale either way and keeping it would show a signed-in shell
      // to someone who is not.
      queryClient.setQueryData(ME_QUERY_KEY, null);
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}
